import React from "react";
import { Globe2 } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import MobileSelectSheet from "@/components/mobile/MobileSelectSheet";
import { useLanguage } from "@/lib/LanguageContext";
import { getSamePageLocalizedPath } from "@/lib/i18nRoutes";

const languages = [
  ["en", "English"],
  ["he", "עברית"],
  ["es", "Español"],
  ["it", "Italiano"],
  ["fr", "Français"],
  ["de", "Deutsch"],
  ["pt", "Português"],
  ["ar", "العربية"],
  ["el", "Ελληνικά"],
  ["ru", "Русский"],
  ["tr", "Türkçe"],
  ["zh", "中文"],
];

const languageCodes = languages.map(([code]) => code);

const chooseLanguageLabels = {
  en: "Choose language",
  he: "בחרו שפה",
  es: "Elegir idioma",
  it: "Scegli lingua",
  fr: "Choisir la langue",
  de: "Sprache wählen",
  pt: "Escolher idioma",
  ar: "اختر اللغة",
  el: "Επιλογή γλώσσας",
  ru: "Выбрать язык",
  tr: "Dil seç",
  zh: "选择语言",
};

function searchWithoutLang(search = "") {
  const params = new URLSearchParams(search);
  params.delete("lang");
  const value = params.toString();
  return value ? `?${value}` : "";
}

export default function LanguageSwitcher({ variant = "default" }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { language, setLanguage } = useLanguage();
  const [sheetOpen, setSheetOpen] = React.useState(false);
  const languageItems = languages.map(([code, label]) => ({ value: code, label }));
  const pathParts = location.pathname.split("/").filter(Boolean);
  const currentLang = languageCodes.includes(pathParts[0]) ? pathParts[0] : language || "en";
  const chooseLanguageLabel = chooseLanguageLabels[currentLang] || "";

  const switchLanguage = (lang) => {
    setLanguage(lang);
    const nextPath = getSamePageLocalizedPath(location.pathname, lang);
    const cleanSearch = searchWithoutLang(location.search);
    navigate(`${nextPath}${cleanSearch}${location.hash || ""}`, { replace: true });
    window.dispatchEvent(new CustomEvent("marstick:language-route-updated", { detail: { language: lang, path: nextPath } }));
  };

  if (variant === "mapIcon") {
    return (
      <>
        <button
          type="button"
          onClick={() => setSheetOpen(true)}
          className="relative grid h-11 w-11 place-items-center rounded-2xl border border-cyan-200/25 bg-slate-950/82 text-cyan-50 shadow-2xl backdrop-blur-2xl ring-1 ring-cyan-300/20 transition hover:border-cyan-200/50 hover:bg-cyan-400/20"
          title={chooseLanguageLabel}
          aria-label={chooseLanguageLabel}
        >
          <Globe2 className="h-5 w-5" aria-hidden="true" />
          <span className="absolute -right-1 -top-1 rounded-full bg-cyan-300 px-1.5 py-0.5 text-[9px] font-black uppercase leading-none text-slate-950">{currentLang}</span>
        </button>
        <MobileSelectSheet open={sheetOpen} onOpenChange={setSheetOpen} title={chooseLanguageLabel} value={currentLang} onChange={switchLanguage} items={languageItems} />
      </>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setSheetOpen(true)}
        className="relative inline-grid h-10 w-14 place-items-center rounded-2xl border border-white/60 bg-white/90 text-xs font-black uppercase text-ocean shadow-xl shadow-ocean/10 backdrop-blur-xl transition hover:bg-white dark:border-white/15 dark:bg-card/90 dark:text-seafoam"
        aria-label={chooseLanguageLabel}
      >
        <Globe2 className="absolute left-2 h-3.5 w-3.5 text-primary" aria-hidden="true" />
        <span className="pl-4 tracking-wider">{currentLang}</span>
      </button>
      <MobileSelectSheet open={sheetOpen} onOpenChange={setSheetOpen} title={chooseLanguageLabel} value={currentLang} onChange={switchLanguage} items={languageItems} />
    </>
  );
}